import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { ContentCopy, WhatsApp, Check } from '@mui/icons-material';
import axios from 'axios';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { ACCENTS, tint } from '../theme/accents';

// The month's summary, ready to paste into the group chat. Generating it sends
// nothing - the admin chooses when and where it goes.
// WhatsApp renders *bold* and _italic_ but shows nothing of the markers. The
// preview mirrors what will land in the chat; Copy and Share still hand over the
// raw text with the markers intact, because that is what WhatsApp needs.
const WHATSAPP_TOKEN = /(\*[^*\n]+\*|_[^_\n]+_)/g;

const renderWhatsApp = (text) =>
  String(text || '').split('\n').map((line, lineIndex, allLines) => (
    <React.Fragment key={lineIndex}>
      {line.split(WHATSAPP_TOKEN).filter(Boolean).map((part, partIndex) => {
        if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
          return <strong key={partIndex}>{part.slice(1, -1)}</strong>;
        }
        if (part.length > 2 && part.startsWith('_') && part.endsWith('_')) {
          return <em key={partIndex}>{part.slice(1, -1)}</em>;
        }
        return <React.Fragment key={partIndex}>{part}</React.Fragment>;
      })}
      {lineIndex < allLines.length - 1 ? '\n' : null}
    </React.Fragment>
  ));

const MonthlyStatement = ({ open, onClose }) => {
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setCopied(false);

    axios
      .get(`/api/dashboard/statement?month=${month}`)
      .then(res => { if (!cancelled) setStatement(res.data); })
      .catch(() => { if (!cancelled) toast.error('Could not build the statement'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, month]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(statement.text);
      setCopied(true);
      toast.success('Copied — paste it into the group chat');
      setTimeout(() => setCopied(false), 2500);
    } catch (error) {
      toast.error('Could not copy. Select the text and copy manually.');
    }
  };

  // Opens WhatsApp with the text prefilled; the admin still picks the chat and
  // presses send, so nothing goes out without a human deciding.
  const handleOpenWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(statement.text)}`, '_blank', 'noopener');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Monthly summary</DialogTitle>
      <DialogContent>
        <TextField
          type="month"
          label="Month"
          size="small"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />

        {loading && (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        )}

        {!loading && statement && (
          <>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: tint(ACCENTS.emerald, 0.3),
                bgcolor: tint(ACCENTS.emerald, 0.07),
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                lineHeight: 1.7,
              }}
            >
              {renderWhatsApp(statement.text)}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              Nothing is sent automatically. Copy it, or open WhatsApp and choose the chat yourself.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Tooltip title="Copy the text">
          <span>
            <Button
              onClick={handleCopy}
              disabled={!statement}
              startIcon={copied ? <Check /> : <ContentCopy />}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="contained"
          onClick={handleOpenWhatsApp}
          disabled={!statement}
          startIcon={<WhatsApp />}
        >
          Open in WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonthlyStatement;
